import React from 'react'
import { connect } from 'react-redux'
import { Button, Dialog, Intent } from '@blueprintjs/core'
import { getFilters } from '../../redux/actions'
import './FilterImport.css'
import JsonDiff from './JsonDiff'

const fieldLabels = {
  name: 'Name', ce: 'CE', vlt: 'VLT', color: 'Color',
  base_price: 'Price', ods: 'OD ranges', ce_ratings: 'CE Ratings'
}

export class FilterImport extends React.Component {
  state = { open: false, busy: false, preview: null, error: null, result: null }

  request = (action, body, json = false) => fetch(`/api/filters/import_${action}.json`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: {
      Accept: 'application/json',
      Authorization: `Bearer ${this.props.token}`,
      ...(json ? { 'Content-Type': 'application/json' } : {})
    },
    body: json ? JSON.stringify(body) : body
  }).then(async response => {
    const data = await response.json().catch(() => ({}))
    if (!response.ok) {
      const message = response.status === 401 || response.status === 403
        ? 'Please sign in again before importing filters.'
        : 'We could not complete this request. Please try again. If this happened after confirming, reload the filter list to check whether the import finished.'
      throw new Error(data.error || message)
    }
    return data
  }, () => {
    throw new Error('We could not reach the server. Check your internet connection and try again. If you already confirmed the import, reload the filter list before retrying to check whether it finished.')
  })

  upload = event => {
    const file = event.target.files[0]
    event.target.value = ''
    if (!file) return
    this.setState({ open: true, busy: true, preview: null, error: null, result: null })
    const body = new FormData()
    body.append('file', file)
    this.request('preview', body)
      .then(preview => this.setState({ preview, busy: false }))
      .catch(error => this.setState({ error: error.message, busy: false }))
  }

  confirm = () => {
    this.setState({ busy: true, error: null })
    this.request('confirm', { import_token: this.state.preview.import_token }, true)
      .then(({ imported }) => {
        this.setState({ preview: null, result: `Imported ${imported} filters.`, busy: false })
        this.props.refresh().catch(() => this.setState({ error: 'Import succeeded, but refreshing failed. Reload the page.' }))
      })
      .catch(error => this.setState({ error: error.message, busy: false }))
  }

  render() {
    const { open, busy, preview, error, result } = this.state
    return (
      <span style={{ marginRight: 8 }}>
        <input type="file" accept=".csv,text/csv" style={{ display: 'none' }}
          ref={input => { this.input = input }} onChange={this.upload} aria-label="Import CSV file" />
        <Button iconName="upload" text="Import CSV" onClick={() => this.input.click()} disabled={busy} />
        <Dialog isOpen={open} title="Import filter specifications" style={{ width: '90vw' }}
          onClose={() => !busy && this.setState({ open: false })}>
          <div className="pt-dialog-body" style={{ maxHeight: '70vh', overflow: 'auto' }}>
            {busy && <p role="status">Processing…</p>}
            {error && <p role="alert" className="pt-intent-danger">{error}</p>}
            {result && <p role="status">{result}</p>}
            {preview && <div>
              <section className="filter-import-summary" aria-label="Import summary">
                <h3>Review your import</h3>
                <p className="filter-import-status">No changes have been saved.</p>
                <div className="filter-import-counts">
                  <span><strong>{preview.rows.filter(row => row.match === 'new').length}</strong> new filters</span>
                  <span><strong>{preview.rows.filter(row => row.match !== 'new' && !row.unchanged).length}</strong> updates</span>
                  <span><strong>{preview.rows.filter(row => row.unchanged).length}</strong> unchanged filters (hidden)</span>
                </div>
                <ul className="filter-import-rules">
                  <li><strong>Blank cells clear fields.</strong> Columns not included in the CSV stay unchanged.</li>
                  <li>Existing names, Shopify links and spectrophotometer data are preserved. No filters are deleted.</li>
                </ul>
                <h4>Columns to import</h4>
                <ul className="filter-import-columns">
                  {Object.keys(preview.columns).filter(key => preview.columns[key]).map(key =>
                    <li key={key}><strong>{key}</strong><span aria-label="maps to"> → </span>{fieldLabels[preview.columns[key]] || preview.columns[key]}</li>
                  )}
                </ul>
                <p className="filter-import-ignored"><strong>Ignored columns</strong><br />
                  {preview.ignored_columns.join(', ') || 'None'}</p>
                {preview.warnings.length > 0 && <details className="filter-import-notes">
                  <summary><strong>{preview.warnings.length} parsing notes</strong><span> — review before confirming</span></summary>
                  <ul>{preview.warnings.map((warning, i) => <li key={i}>{warning}</li>)}</ul>
                </details>}
              </section>
              {preview.rows.filter(row => !row.unchanged).map((row, i) => <section className="filter-import-change" key={i}>
                <h4>{row.match === 'exact' ? row.target_name : `${row.source_name} → ${row.target_name}`}
                  <span>{row.match === 'new' ? 'Create new' : row.match === 'non-exact' ? 'Update (non-exact name match)' : 'Update'}</span>
                </h4>
                <JsonDiff before={row.current_attributes} after={row.imported_attributes} />
              </section>)}
              {preview.rows.every(row => row.unchanged) && <p>No changes to import.</p>}

            </div>}
          </div>
          <div className="pt-dialog-footer">
            <div className="pt-dialog-footer-actions">
              <Button text={result ? 'Close' : 'Cancel'} disabled={busy} onClick={() => this.setState({ open: false })} />
              {preview && <Button intent={Intent.PRIMARY} text="Confirm import" disabled={busy || preview.rows.every(row => row.unchanged)} onClick={this.confirm} />}
            </div>
          </div>
        </Dialog>
      </span>
    )
  }
}

export default connect(state => ({ token: state.token }), dispatch => ({
  refresh: () => dispatch(getFilters())
}))(FilterImport)
